<template>
    <div>
        <b-modal
            v-model="showModal"
            :title="isUpdate ? 'Edit Company User' : 'Add Company User'"
            content-class="bg-gray-100"
            hide-footer
            centered
            @hide="onModalHide"
            @show="onModalShow"
        >
            <!-- <p v-if="error" class="text-danger">{{ errorMsg }}</p> -->  <!-- Errors will be shown in form (most) -->
            <b-overlay :show="loading || showSuccess">
                <template #overlay>
                    <div v-if="showSuccess" class="text-center">
                        <b-icon icon="check-circle" variant="success" font-scale="3"></b-icon>
                        <h4 class="mt-3">Success</h4>
                    </div>
                    <b-spinner v-else></b-spinner>
                </template>
                <FormulateForm
                    class="company-user-form"
                    v-model="tuData"
                    name="company_user_form"
                    @submit="saveData"
                    :errors="inputErrors"
                    :form-errors="formErrors"
                >
                    <div class="form-row mb-3">
                        <div class="col">
                            <FormulateInput
                                name="fname"
                                type="text"
                                label="First Name"
                                :placeholder="loading ? '' : 'First name'"
                                validation="required"
                                validation-name="First Name"
                                :disabled="loading"
                                :disable-errors="loading"
                            />
                        </div>
                        <div class="col">
                            <FormulateInput
                                name="lname"
                                type="text"
                                label="Last Name"
                                :placeholder="loading ? '' : 'Last name'"
                                validation="required"
                                validation-name="Last Name"
                                :disabled="loading"
                                :disable-errors="loading"
                            />
                        </div>
                    </div>
                    <FormulateInput
                        name="_email"
                        type="email"
                        label="Email Address"
                        help="The email domain (ie: @yourcompany.com) must match the domain from some other user in your company.  Email addresses cannot be changed once a user has been invited."
                        :placeholder="loading ? '' : 'person@company.com'"
                        validation="required|email"
                        validation-name="Email Address"
                        :disabled="loading || updateObj.email_is_locked"
                        :disable-errors="loading"
                    />
                    <div class="form-row mb-3">
                        <div class="col">
                            <FormulateInput
                                name="job_title"
                                type="text"
                                label="Job Title"
                                :placeholder="loading ? '' : 'Job Title'"
                                :disabled="loading"
                                :disable-errors="loading"
                            />
                        </div>
                        <div class="col">
                            <FormulateInput
                                name="work_location"
                                type="text"
                                label="Work Location"
                                :placeholder="loading ? '' : 'Work Location'"
                                help="Plant, office, or other location where person is based"
                                :disabled="loading"
                                :disable-errors="loading"
                            />
                        </div>
                    </div>
                    <FormulateInput
                        name="is_company_admin"
                        type="checkbox"
                        label="Company Admin?"
                        help="Company Admins can add/remove other users from their company."
                        hide-label-icons
                    />
                    <!-- Oct 2026 - TenantRoles (was is_def_* checkboxes) - only the roles external admins may assign; only rendered once the options are loaded, -->
                    <!-- so if they fail to load, tenant_role_slugs is not sent and the user's roles (or the tenant defaults, on create) stay as they are -->
                    <FormulateInput
                        v-if="tenantRoleOptionsLoaded && tenantRoleOptions.length"
                        name="tenant_role_slugs"
                        type="checkbox"
                        label="Contact Types:"
                        help="This contact should be considered a relevant contact for the following topics:"
                        :options="tenantRoleCheckboxOptions"
                        input-class="d-flex gap-4"
                        :element-class="['d-flex', 'flex-wrap', 'col-gap-4', 'row-gap-3']"
                        hide-label-icons
                    />
                    <FormulateInput
                        type="submit"
                        :disabled="loading"
                    >
                        <b-spinner small v-if="loading" class="mr-2"></b-spinner>
                        <span v-if="loading" key="loading">Loading</span>
                        <span v-else key="notLoading">{{ isUpdate ? 'Update User' : 'Add User' }}</span>
                    </FormulateInput>
                </FormulateForm>
            </b-overlay>
        </b-modal>
    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';

export default {
    name: 'CompanyPortalUserForm',
    components: {},
    props: {
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            showSuccess: false,  // show success message?
            showModal: false,  // v-model for modal
            tuData: {
                is_company_admin: false,  // need this or else vue-formulate may not add it to the object unless toggle on/off
            },  // v-model with form
            isUpdate: false,
            updateId: '',  // set this when you're dealing with an update
            updateObj: {},  // full object being updated, with all fields, not just the ones in form
            formErrors: [],  // non-field errors for the form, array of strings
            inputErrors: {},  // field-related errors for the form, object
            tenantRoleOptions: [],  // [{id, slug, name, description, is_default}] - for external requesters, only the roles external admins may assign
            tenantRoleOptionsLoaded: false,
        }
    },
    computed: {
        tenantRoleCheckboxOptions() {
            return this.tenantRoleOptions.map(opt => ({ value: opt.slug, label: opt.name }));
        },
    },
    methods: {
        // PUBLIC METHODS CALLED FROM PARENT
        startCreate: async function() {
            //this.resetForm();  // reset not needed since entire form gets destroyed when modal closes
            this.showModal = true;
            await this.loadTenantRoleOptions();
            if (!this.tenantRoleOptionsLoaded) return;  // leave tenant_role_slugs out entirely -- the API then applies the tenant defaults
            // preselect the roles new contacts get by default
            this.tuData = { ...this.tuData, tenant_role_slugs: this.tenantRoleOptions.filter(opt => opt.is_default).map(opt => opt.slug) };
        },
        startUpdate: async function(userObj) {
            //this.resetForm();  // reset not needed since entire form gets destroyed when modal closes
            console.log(userObj);
            this.isUpdate = true;
            this.setUpdateObject(userObj);
            this.showModal = true;
            await this.loadTenantRoleOptions();
            if (!this.tenantRoleOptionsLoaded) return;  // leave tenant_role_slugs out entirely -- the user's roles stay as they are
            // seed from the roles this user holds -- only the ones offered here (roles outside the options are left untouched by the API)
            const optionSlugs = this.tenantRoleOptions.map(opt => opt.slug);
            const heldSlugs = (userObj.tenant_roles || []).map(role => role.slug).filter(slug => optionSlugs.includes(slug));
            this.tuData = { ...this.tuData, tenant_role_slugs: heldSlugs };
        },
        loadTenantRoleOptions: async function() {
            try {
                const { data } = await suppliosApi.contact.tenantRoleOptions();
                this.tenantRoleOptions = data;
                this.tenantRoleOptionsLoaded = true;
            } catch (error) {
                this.handleError(error, 'Error while getting contact role options');
            }
        },
        //  Private methods
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        saveData: function(data) {
            this.loading = true;
            //let apiMethod = this.isUpdate ? 'updateCompanyUser' : 'addCompanyUser';
            let apiCall = this.isUpdate ? suppliosApi.portal.companyUserUpdate(this.updateId, data) : suppliosApi.portal.companyUserCreate(data);
            return apiCall.then(response => {
                    this.tuData = response.data;
                    // NOTE - response.data will only have the fields you have on this form, I believe
                    // no 'full_name' -- just fname, lname, _email, id, is_company_admin, job_title, work_location
                    //this.$toast.success('Successfully saved this user');
                    this.showSuccess = true;
                    setTimeout(() => {
                        this.$emit('save-complete', this.tuData);
                        this.showModal = false;
                    }, 2000);
                    return true;
                })
                .catch(err => {
                    this.handleError(err, 'Error while saving user');
                    this.inputErrors = err.response.data;
                    this.formErrors = err.response.data.non_field_errors;
                    return false;
                })
                .then(success => {
                    this.loading = false;
                    return success;
                })
        },
        setUpdateObject: function(updateObj) {
            // set the tuData based on object passed in
            this.updateId = updateObj.id;  // set the ID of TU being updated
            this.updateObj = updateObj;
            let tuData = {
                fname: updateObj.fname,
                lname: updateObj.lname,
                _email: updateObj.email,
                job_title: updateObj.job_title,
                work_location: updateObj.work_location,
                is_company_admin: updateObj.role_slug === 'external_admin' ? true : false,
            }
            // tenant_role_slugs is seeded in startUpdate, once the role options are loaded
            this.tuData = tuData;
        },
        onModalShow: function() {
            this.showSuccess = false; 
        },
        onModalHide: function() {
            this.resetForm();
            this.$formulate.reset('company_user_form');
        },
        resetForm: function() {
            this.isUpdate = false;
            this.updateId = '';
            this.updateObj = {};
            this.error = false;
            this.formErrors = [];
            this.inputErrors = {};
        }

    }
}
</script>

<style>

</style>
